import "./contact.css";

import React from "react";

import { CONTACT, CONTACT_PAGE } from "../../data/content";
import { postRequest } from "../../utils/apiHandlers";
import PageMeta from "../general/PageMeta";
import PageBanner from "../general/PageBanner";
import PhotoStrip from "../general/PhotoStrip";

const EMPTY_FORM = { firstName: "", lastName: "", email: "", enquiryType: "", message: "", website: "" };

const Contact = () => {
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [status, setStatus] = React.useState("idle"); //idle | sending | sent | error
  const [error, setError] = React.useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await postRequest("/Contact/SendContactMessage", form);
      setStatus("sent");
      setForm(EMPTY_FORM);
    }
    catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  return (
    <React.Fragment>
      <PageMeta title={CONTACT_PAGE.metaTitle} />
      <PageBanner />

      <section className={"contact dark-page"}>
        <div className={"contact-inner container"}>
          <div className={"contact-details"}>
            <img
              className={"contact-logo"}
              src={"/images/logo-colour.png"}
              alt={"Alman’s Dried Fruit & Nuts"}
              width={"240"}
              height={"208"}
            />
            <p>
              <span className={"label"}>Tel:</span>
              {" "}
              <a href={CONTACT.phones[0].href}>{CONTACT.phones[0].display}</a>
              {" / "}
              <a href={CONTACT.phones[1].href}>{CONTACT.phones[1].display}</a>
              <br />
              <span className={"label"}>Email:</span>
              {" "}
              <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
            </p>
            <p>
              <span className={"label"}>Address:</span>
              {" "}
              <a
                href={CONTACT.mapUrl}
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                {CONTACT.address}
              </a>
            </p>
            <p>
              <span className={"label"}>Factory Shops:</span>
              {CONTACT.factoryShops.map((shop) => (
                <React.Fragment key={shop}>
                  <br />
                  {shop}
                </React.Fragment>
              ))}
            </p>
            <ul className={"social-links"}>
              <li>
                <a
                  href={CONTACT.instagram}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  aria-label={"Alman’s on Instagram"}
                >
                  <svg
                    viewBox={"0 0 24 24"}
                    aria-hidden={"true"}
                  >
                    <rect
                      x={"3"}
                      y={"3"}
                      width={"18"}
                      height={"18"}
                      rx={"5"}
                    />
                    <circle
                      cx={"12"}
                      cy={"12"}
                      r={"4.2"}
                    />
                    <circle
                      className={"dot"}
                      cx={"17.3"}
                      cy={"6.7"}
                      r={"1.1"}
                    />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.facebook}
                  target={"_blank"}
                  rel={"noopener noreferrer"}
                  aria-label={"Alman’s on Facebook"}
                >
                  <svg
                    viewBox={"0 0 24 24"}
                    aria-hidden={"true"}
                  >
                    <path
                      className={"solid"}
                      d={"M13.5 21v-7.6h2.6l.4-3h-3V8.5c0-.9.3-1.5 1.5-1.5h1.6V4.3c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4v2.2H7.8v3h2.6V21h3.1z"}
                    />
                  </svg>
                </a>
              </li>
            </ul>
          </div>

          <div className={"contact-form-wrap"}>
            <h1 className={"contact-title"}>{CONTACT_PAGE.title}</h1>

            {status === "sent" ? (
              <div
                className={"form-success"}
                role={"status"}
              >
                <p>{CONTACT_PAGE.success}</p>
                <button
                  type={"button"}
                  className={"link-button"}
                  onClick={() => setStatus("idle")}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                className={"contact-form"}
                onSubmit={onSubmit}
              >
                <div className={"field-row"}>
                  <div className={"field"}>
                    <label htmlFor={"firstName"}>First Name</label>
                    <input
                      id={"firstName"}
                      name={"firstName"}
                      type={"text"}
                      autoComplete={"given-name"}
                      maxLength={100}
                      value={form.firstName}
                      onChange={onChange}
                    />
                  </div>
                  <div className={"field"}>
                    <label htmlFor={"lastName"}>Last Name</label>
                    <input
                      id={"lastName"}
                      name={"lastName"}
                      type={"text"}
                      autoComplete={"family-name"}
                      maxLength={100}
                      value={form.lastName}
                      onChange={onChange}
                    />
                  </div>
                </div>
                <div className={"field"}>
                  <label htmlFor={"email"}>Email *</label>
                  <input
                    id={"email"}
                    name={"email"}
                    type={"email"}
                    autoComplete={"email"}
                    maxLength={200}
                    required
                    value={form.email}
                    onChange={onChange}
                  />
                </div>
                <div className={"field"}>
                  <label htmlFor={"enquiryType"}>{CONTACT_PAGE.enquiryTypeLabel}</label>
                  <div className={"select-wrap"}>
                    <select
                      id={"enquiryType"}
                      name={"enquiryType"}
                      value={form.enquiryType}
                      onChange={onChange}
                    >
                      <option value={""}>{CONTACT_PAGE.enquiryTypeLabel}</option>
                      {CONTACT_PAGE.enquiryTypes.map((type) => (
                        <option
                          key={type}
                          value={type}
                        >
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className={"field"}>
                  <label htmlFor={"message"}>Write a message *</label>
                  <textarea
                    id={"message"}
                    name={"message"}
                    rows={4}
                    maxLength={5000}
                    required
                    value={form.message}
                    onChange={onChange}
                  />
                </div>
                {/* Honeypot - hidden from people, bots fill it in */}
                <div
                  className={"hp-field"}
                  aria-hidden={"true"}
                >
                  <label htmlFor={"website"}>Website</label>
                  <input
                    id={"website"}
                    name={"website"}
                    type={"text"}
                    tabIndex={-1}
                    autoComplete={"off"}
                    value={form.website}
                    onChange={onChange}
                  />
                </div>

                {status === "error" ? (
                  <p
                    className={"form-error"}
                    role={"alert"}
                  >
                    {error}
                  </p>
                ) : null}

                <button
                  type={"submit"}
                  className={"submit-button"}
                  disabled={status === "sending"}
                >
                  {status === "sending" ? "Sending..." : "Submit"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <PhotoStrip />
    </React.Fragment>
  );
};

export default Contact;
